import { type ReactElement, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { buildWorkspaceTagsSettingsHref } from '@/features/organisations/utils/organisationSettingsRoute';
import { defaultTagColor } from '@/features/shared/components/entityDetails/EditableTagsSection.utils';
import { toolbarControlRadius } from '@/features/shared/components/tableSystem/tableToolbar.helpers';
import { s } from '@/theme/tokens';
import { Box, Text } from '@/ui';
import { TagsFilterField } from '@/ui/composites/filters/TagsFilterField';
import { AddIcon, CursorTooltip, InfoOutlinedIcon } from '@/ui/primitives';
import type { TagColor, TagOption } from '@/shared/data/tags';

type EntityTagsFieldProps = {
  colors?: TagColor[];
  tags: string[];
  tagOptions: TagOption[];
  helperText?: string;
  label?: string;
  onColorsChange: (value: TagColor[]) => void;
  onTagsChange: (value: string[]) => void;
};

const tagInputHeight = 40;

export const EntityTagsField = ({
  colors = [],
  tags,
  tagOptions,
  helperText,
  label = 'Tags',
  onColorsChange,
  onTagsChange,
}: EntityTagsFieldProps): ReactElement => {
  const { selectedOrganisationId } = useOrganisationFilter();
  const options = useMemo(() => {
    const optionByValue = new Map<string, TagOption>(
      tagOptions.map((option) => [option.value, option]),
    );

    tags.forEach((tag, index) => {
      if (optionByValue.has(tag)) {
        return;
      }
      optionByValue.set(tag, {
        value: tag,
        label: tag,
        color: colors[index] ?? defaultTagColor,
      });
    });

    return Array.from(optionByValue.values()).map((option) => ({
      value: option.value,
      label: option.label,
      color: option.color,
    }));
  }, [colors, tagOptions, tags]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: s('sm'), minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: s('xs') }}>
        <Text
          role="body"
          sx={(theme) => ({
            fontSize: theme.typography.subtitle1.fontSize,
            lineHeight: theme.typography.subtitle1.lineHeight,
          })}
        >
          {label}
        </Text>
        {helperText ? (
          <CursorTooltip
            title={helperText}
            placement="right-start"
            arrow
            disableFocusListener
            disableTouchListener
            slotProps={{
              tooltip: {
                sx: {
                  fontSize: '15px',
                  lineHeight: 1.4,
                  maxWidth: 440,
                  px: '10px',
                  py: '6px',
                },
              },
            }}
          >
            <Box
              component="span"
              aria-label={`More information about ${label.toLowerCase()}`}
              sx={{
                alignItems: 'center',
                color: 'text.secondary',
                cursor: 'help',
                display: 'inline-flex',
                height: s('xl'),
                justifyContent: 'center',
                width: s('xl'),
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 16 }} />
            </Box>
          </CursorTooltip>
        ) : null}
      </Box>
      <TagsFilterField
        value={tags}
        options={options}
        label={label}
        showLabel={false}
        labelPlacement="stacked"
        emptyLabel="Add Tags"
        emptyOptionsAction={
          <Box
            component={RouterLink}
            to={buildWorkspaceTagsSettingsHref(selectedOrganisationId ?? null)}
            target="_blank"
            rel="noreferrer"
            sx={{
              alignItems: 'center',
              color: 'text.primary',
              display: 'inline-flex',
              gap: s('sm'),
              minWidth: 0,
              textDecoration: 'none',
              width: '100%',
              '&:hover, &:focus-visible': {
                textDecoration: 'none',
              },
            }}
          >
            <AddIcon sx={{ color: 'text.primary', fontSize: (theme) => theme.app.iconSizes.sm }} />
            <Text role="body" component="span" color="textPrimary">
              Add New
            </Text>
          </Box>
        }
        inputAriaLabel={label}
        fullWidth
        controlHeight={tagInputHeight}
        controlRadius={toolbarControlRadius}
        dropdownBackgroundColor="var(--surface-page-muted)"
        inputBackgroundColor="var(--surface-page-muted)"
        showClearButton={false}
        optionPresentation="tagChip"
        showSelectedChipRemoveButtons
        onChange={(nextTags) => {
          onTagsChange(nextTags);
          onColorsChange(
            nextTags.map((tag) => {
              const matchingOption = tagOptions.find((option) => option.value === tag);
              const existingTagIndex = tags.indexOf(tag);
              if (matchingOption) {
                return matchingOption.color;
              }
              return existingTagIndex >= 0 ? colors[existingTagIndex] : defaultTagColor;
            }),
          );
        }}
      />
    </Box>
  );
};
